import { Link } from 'react-router'
import { styled } from 'baseui'
import type { StyleObject } from 'styletron-standard'
import { RAIL_EASE, RAIL_OVERLAY_Z_INDEX, railContentFadeIn, railStaggerDelay } from '@/shared/theme/railSlot'
import { floatingButton, menuSlot } from '../styled-components'

export const StyledMenuEntryLink = styled<typeof Link, { $fill: boolean; $expanded: boolean }>(
  Link,
  ({ $fill, $expanded }) => menuSlot($fill, $expanded),
)

export const StyledMenuEntrySlot = styled<'div', { $fill: boolean; $expanded: boolean }>(
  'div',
  ({ $fill, $expanded }) => menuSlot($fill, $expanded),
)

export const StyledMenuFooterSpacer = styled('div', {
  marginTop: 'auto',
})

export const StyledMenuFamilyButton = styled<'button', { $expanded: boolean; $open: boolean; $selected: boolean }>(
  'button',
  ({ $theme, $expanded, $open, $selected }) => ({
    ...(floatingButton($theme, $expanded, $selected) as StyleObject),
    display: 'flex',
    alignItems: 'center',
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    border: 0,
    fontFamily: 'inherit',
    fontSize: 'inherit',
    cursor: 'pointer',
    ...($open ? { backgroundColor: $expanded ? $theme.colors.white : $theme.colors.bg050 } : {}),
    ':focus-visible': { outline: `.125rem solid ${$theme.colors.primary}`, outlineOffset: '-.125rem' },
    ...($expanded
      ? {}
      : {
          '::after': {
            content: '""',
            position: 'absolute',
            right: '0.1875rem',
            bottom: '0.1875rem',
            borderLeft: '0.3125rem solid transparent',
            borderBottom: `0.3125rem solid ${$theme.colors.contentTertiary}`,
          },
        }),
  }),
)

export const StyledFamilyChevron = styled<'span', { $hidden: boolean }>('span', ({ $theme, $hidden }) => ({
  display: $hidden ? 'none' : 'inline-block',
  flexShrink: 0,
  width: '0.375rem',
  height: '0.375rem',
  marginLeft: 'auto',
  marginRight: '0.25rem',
  borderRight: `0.09375rem solid ${$theme.colors.contentTertiary}`,
  borderBottom: `0.09375rem solid ${$theme.colors.contentTertiary}`,
  transform: 'rotate(-45deg)',
  animationName: { from: { opacity: 0 }, to: { opacity: 1 } },
  ...railContentFadeIn,
  animationDuration: '220ms',
  animationDelay: railStaggerDelay(40),
}))

export const StyledMenuDivider = styled('div', ({ $theme }) => ({
  alignSelf: 'stretch',
  flexShrink: 0,
  height: '1px',
  marginTop: '0.5rem',
  marginBottom: '0.5rem',
  backgroundColor: $theme.colors.borderPulseCard,
}))

export const StyledFlyoutCard = styled('div', ({ $theme }) => ({
  position: 'fixed',
  zIndex: RAIL_OVERLAY_Z_INDEX,
  minWidth: '14rem',
  padding: '0.375rem',
  boxSizing: 'border-box',
  backgroundColor: $theme.colors.white,
  border: `1px solid ${$theme.colors.borderLight}`,
  borderRadius: '0.5rem',
  boxShadow: $theme.lighting.shadow700,
  animationName: {
    from: { opacity: 0, transform: 'translateX(-0.25rem)' },
    to: { opacity: 1, transform: 'none' },
  },
  animationDuration: '160ms',
  animationTimingFunction: RAIL_EASE,
  '@media (prefers-reduced-motion: reduce)': { animationName: 'none' },
  '::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: '100%',
    width: '1.25rem',
  },
}))

export const StyledFlyoutArrow = styled('span', ({ $theme }) => ({
  position: 'absolute',
  left: '-0.375rem',
  width: '0.625rem',
  height: '0.625rem',
  backgroundColor: $theme.colors.white,
  borderLeft: `1px solid ${$theme.colors.borderLight}`,
  borderBottom: `1px solid ${$theme.colors.borderLight}`,
}))

export const StyledFlyoutTitle = styled('div', ({ $theme }) => ({
  padding: '0.375rem 0.625rem 0.25rem',
  fontSize: '0.6875rem',
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: $theme.colors.textMuted,
}))

const flyoutRow = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem',
  padding: '0.5625rem 0.625rem',
  borderRadius: '0.375rem',
  fontSize: '0.875rem',
  lineHeight: '1.25rem',
  whiteSpace: 'nowrap',
  textDecoration: 'none',
} as const

export const StyledFlyoutRow = styled(Link, ({ $theme }) => ({
  ...flyoutRow,
  color: $theme.colors.contentPrimary,
  ':hover': { backgroundColor: $theme.colors.bg050 },
  ':focus-visible': { backgroundColor: $theme.colors.bg050, outline: `.125rem solid ${$theme.colors.primary}` },
}))

export const StyledFlyoutCurrentRow = styled('span', ({ $theme }) => ({
  ...flyoutRow,
  color: $theme.colors.textMuted,
  cursor: 'default',
  ':focus-visible': { outline: `.125rem solid ${$theme.colors.primary}` },
}))

export const StyledFlyoutNote = styled('span', ({ $theme }) => ({
  marginLeft: 'auto',
  paddingLeft: '1rem',
  fontSize: '0.6875rem',
  color: $theme.colors.textMuted,
}))
